<template
  ><main
    class="gs-app gm-studio"
    :style="
      popupMode
        ? { minHeight: 'auto', padding: '0', background: 'transparent' }
        : { minHeight: '100vh' }
    "
  >
    <p v-if="loading" role="status">Loading form…</p>
    <div v-else-if="error && !form" class="gm-panel gm-empty" role="alert">
      <h1>Form unavailable</h1>
      <p>This form may be unpublished or temporarily unavailable.</p>
      <gs-button variant="outline" @click="load">Try again</gs-button>
    </div>
    <div v-else-if="message" class="gm-panel gm-empty" role="status">
      <h1>Thank you</h1>
      <p>{{ message }}</p>
    </div>
    <div v-else-if="form && form.legacy" ref="legacyHost"></div>
    <template v-else-if="form"
      ><p v-if="error" class="gm-error" role="alert">{{ error }}</p>
      <form-canvas
        :fields="form.fields"
        :design="form.design"
        interactive
        :busy="busy"
        @submit="submit"
    /></template></main
></template>
<script>
import './index';
import axios from 'axios';
import FormCanvas from './FormCanvas.vue';
import { errorMessage } from './contracts';
import { formFontStylesheetUrl } from './data/form-design';
export default {
  components: { FormCanvas },
  props: { formId: { type: String, required: true }, popupMode: Boolean },
  data: () => ({
    form: null,
    loading: true,
    busy: false,
    error: '',
    message: '',
    customStyle: null,
    fontLink: null
  }),
  created() {
    this.load();
  },
  mounted() {
    // Let the embedding page size its iframe to the whole form (no inner scroll).
    if (window.parent === window) return;
    const report = () => {
      this.resizeFrame = null;
      const height = Math.ceil(document.documentElement.scrollHeight);
      if (height && height !== this.reportedHeight) {
        this.reportedHeight = height;
        window.parent.postMessage({ type: 'groovemail:resize', formId: this.formId, height }, '*');
      }
    };
    this.reportHeight = () => {
      if (!this.resizeFrame) this.resizeFrame = window.requestAnimationFrame(report);
    };
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.reportHeight);
      this.resizeObserver.observe(document.documentElement);
    }
    window.addEventListener('load', this.reportHeight);
    this.reportHeight();
  },
  updated() {
    if (this.reportHeight) this.reportHeight();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.reportHeight) window.removeEventListener('load', this.reportHeight);
    if (this.customStyle) this.customStyle.remove();
    if (this.fontLink) this.fontLink.remove();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        this.form = (
          await axios.get(
            '/studio/public/forms/' + encodeURIComponent(this.formId)
          )
        ).data;
        if (this.form.legacy) {
          this.loading = false;
          await this.$nextTick();
          const element = document.createElement('groovemail-form-embed');
          element.setAttribute('permalink', window.btoa(this.formId));
          this.$refs.legacyHost.appendChild(element);
          const script = document.createElement('script');
          script.src =
            window.config.grooveappsurl.replace(/\/$/, '') +
            '/groovemail/embed/app.js';
          script.onerror = () => {
            this.form = null;
            this.error = 'Unable to load the legacy form.';
          };
          this.$refs.legacyHost.appendChild(script);
          return;
        }
        const css = this.form.design.customCss || '';
        if (
          css &&
          !/[<>@\\]|url\s*\(|expression|behavior|javascript/i.test(css)
        ) {
          this.customStyle = document.createElement('style');
          this.customStyle.textContent = css;
          document.head.appendChild(this.customStyle);
        }
        const font = formFontStylesheetUrl(this.form.design.fontFamily);
        if (font) {
          this.fontLink = document.createElement('link');
          this.fontLink.rel = 'stylesheet';
          this.fontLink.href = font;
          document.head.appendChild(this.fontLink);
        }
      } catch (error) {
        this.error = errorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    async submit(values) {
      this.busy = true;
      this.error = '';
      try {
        const response = await axios.post(
          '/studio/public/forms/' + encodeURIComponent(this.formId) + '/submit',
          values
        );
        this.message = response.data.message;
        const redirect = response.data.redirectUrl;
        if (redirect && /^https?:\/\//i.test(redirect)) {
          const origin = new URLSearchParams(window.location.search).get(
            'parentOrigin'
          );
          if (this.popupMode && origin && /^https?:\/\//.test(origin))
            window.parent.postMessage(
              { type: 'groovemail:redirect', url: redirect },
              origin
            );
          else window.location.assign(redirect);
        }
      } catch (error) {
        this.error = errorMessage(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
